'use client';

import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { TextPlugin } from 'gsap/TextPlugin';
import type { RefObject } from 'react';

gsap.registerPlugin(useGSAP, ScrollTrigger, TextPlugin);

// Hooks read by the motion layer (all optional; the page renders fine without JS motion):
//   [data-hero]            hero elements, staggered in on load
//   [data-hero-underline]  SVG path drawn under the hero title
//   [data-typewriter]      text cycled through `prompts`
//   [data-chip]            chips in the hero prompt box, popped in after the hero
//   [data-doodle]          decorative hero doodles that drift
//   [data-editor-*]        the hero's editor preview: messages, then nodes, edges and cursor
//   [data-reveal]          blocks faded up when scrolled into view
//   svg[data-illustration] feature illustrations; see landing-illustrations.tsx for their tags
//   [data-mermaid-demo]    Mermaid sample: code lines typed in, diagram nodes popped, edges drawn

type AnimKind = 'pop' | 'draw' | 'fade' | 'drop';

function strokeLength(el: Element) {
  const declared = Number(el.getAttribute('pathLength'));
  if (declared > 0) {
    return declared;
  }
  return el instanceof SVGGeometryElement ? el.getTotalLength() : 0;
}

function addIntroTween(tl: gsap.core.Timeline, el: Element, kind: AnimKind, position: number) {
  switch (kind) {
    case 'draw': {
      const length = strokeLength(el);
      // Hidden until its turn: a zero-length dash still renders round line caps as dots.
      tl.fromTo(
        el,
        { strokeDasharray: length, strokeDashoffset: length, autoAlpha: 0 },
        {
          strokeDashoffset: 0,
          autoAlpha: 1,
          duration: 0.6,
          ease: 'power2.inOut',
          immediateRender: true
        },
        position
      );
      return;
    }
    case 'pop':
      tl.from(
        el,
        {
          scale: 0.6,
          autoAlpha: 0,
          transformOrigin: '50% 50%',
          duration: 0.5,
          ease: 'back.out(1.8)'
        },
        position
      );
      return;
    case 'drop':
      tl.from(
        el,
        {
          y: -40,
          rotation: -8,
          autoAlpha: 0,
          transformOrigin: '50% 0%',
          duration: 0.7,
          ease: 'back.out(1.4)'
        },
        position
      );
      return;
    default:
      tl.from(el, { y: 12, autoAlpha: 0, duration: 0.45, ease: 'power2.out' }, position);
  }
}

function startIdleLoops(svg: Element) {
  svg.querySelectorAll('[data-idle]').forEach((el, index) => {
    const idle = el.getAttribute('data-idle');
    if (idle === 'float') {
      gsap.to(el, {
        y: -6,
        duration: 2.2 + index * 0.4,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1
      });
    } else if (idle === 'pulse') {
      gsap.to(el, {
        scale: 1.05,
        transformOrigin: '50% 50%',
        duration: 1.1,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1
      });
    } else if (idle === 'blink') {
      gsap.to(el, { autoAlpha: 0, duration: 0.5, ease: 'steps(1)', yoyo: true, repeat: -1 });
    }
  });
}

function illustrationTimeline(svg: Element) {
  const steps = new Map<number, Element[]>();
  svg.querySelectorAll('[data-anim]').forEach((el) => {
    const step = Number(el.getAttribute('data-step') ?? 0);
    steps.set(step, [...(steps.get(step) ?? []), el]);
  });

  const tl = gsap.timeline({
    paused: true,
    onComplete: () => startIdleLoops(svg)
  });
  [...steps.keys()]
    .sort((a, b) => a - b)
    .forEach((step) => {
      // Each step starts slightly before the previous one finishes so the drawing feels continuous.
      const start = Math.max(0, tl.duration() - 0.15);
      steps.get(step)?.forEach((el, index) => {
        addIntroTween(
          tl,
          el,
          (el.getAttribute('data-anim') as AnimKind) ?? 'fade',
          start + index * 0.08
        );
      });
    });
  return tl;
}

function animateIllustration(svg: Element, scroller: Element) {
  const tl = illustrationTimeline(svg);
  ScrollTrigger.create({
    trigger: svg,
    scroller,
    start: 'top 80%',
    once: true,
    onEnter: () => tl.play()
  });
}

function mermaidTimeline(root: Element, vars?: gsap.TimelineVars) {
  const q = gsap.utils.selector(root);
  const tl = gsap.timeline(vars);
  tl.from(q('[data-mermaid-line]'), { autoAlpha: 0, x: -10, duration: 0.3, stagger: 0.12 });
  tl.from(
    q('[data-mermaid-node]'),
    { autoAlpha: 0, scale: 0.6, transformOrigin: '50% 50%', duration: 0.4, stagger: 0.12, ease: 'back.out(1.8)' },
    '-=0.1'
  );
  tl.fromTo(
    q('[data-mermaid-edge]'),
    { strokeDasharray: 1, strokeDashoffset: 1 },
    { strokeDashoffset: 0, duration: 0.45, stagger: 0.1, ease: 'power2.out' },
    '-=0.5'
  );
  return tl;
}

/**
 * Plays the intro of a visual that appears after load (a feature tab being switched in).
 * Returns a cleanup, or nothing when motion is reduced and the visual just shows as is.
 */
export function playLandingVisual(root: Element) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return undefined;
  }
  const ctx = gsap.context(() => {
    root.querySelectorAll('svg[data-illustration]').forEach((svg) => illustrationTimeline(svg).play());
    root.querySelectorAll('[data-mermaid-demo]').forEach((demo) => mermaidTimeline(demo));
  }, root);
  return () => ctx.revert();
}

function startTypewriter(el: Element, prompts: string[]) {
  const tl = gsap.timeline({ repeat: -1, delay: 1.2 });
  prompts.forEach((prompt, index) => {
    // The first prompt is already rendered; start by holding it, then erase and type the next.
    if (index > 0) {
      tl.to(el, { text: { value: prompt }, duration: prompt.length * 0.035, ease: 'none' });
    }
    tl.to({}, { duration: 2.2 });
    tl.to(el, { text: { value: '' }, duration: 0.5, ease: 'none' });
  });
  tl.to(el, {
    text: { value: prompts[0] ?? '' },
    duration: (prompts[0]?.length ?? 0) * 0.035,
    ease: 'none'
  });
}

export function useLandingMotion(scope: RefObject<HTMLElement | null>, prompts: string[]) {
  useGSAP(
    () => {
      const root = scope.current;
      if (!root) {
        return;
      }
      const q = gsap.utils.selector(root);
      const mm = gsap.matchMedia();

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        const intro = gsap.timeline({ defaults: { ease: 'power3.out' } });
        intro.from(q('[data-hero]'), { y: 28, autoAlpha: 0, duration: 0.8, stagger: 0.09 });

        const underline = q('[data-hero-underline]')[0];
        if (underline) {
          const length = strokeLength(underline);
          intro.fromTo(
            underline,
            { strokeDasharray: length, strokeDashoffset: length, autoAlpha: 0 },
            {
              strokeDashoffset: 0,
              autoAlpha: 1,
              duration: 0.8,
              ease: 'power2.inOut',
              immediateRender: true
            },
            0.45
          );
        }
        intro.from(
          q('[data-chip]'),
          { y: 10, scale: 0.92, autoAlpha: 0, duration: 0.4, stagger: 0.05, ease: 'back.out(1.6)' },
          '-=0.4'
        );

        const editor = gsap.timeline({ delay: 0.7, defaults: { ease: 'power3.out' } });
        editor.from(q('[data-editor-message]'), { y: 10, autoAlpha: 0, duration: 0.45, stagger: 0.35 });
        editor.from(
          q('[data-editor-node]'),
          { scale: 0.7, autoAlpha: 0, transformOrigin: '50% 50%', duration: 0.45, stagger: 0.12, ease: 'back.out(1.7)' },
          '-=0.2'
        );
        q('[data-editor-edge] path').forEach((path, index) => {
          const length = strokeLength(path);
          editor.fromTo(
            path,
            { strokeDasharray: length, strokeDashoffset: length },
            { strokeDashoffset: 0, duration: 0.4, ease: 'power2.inOut', immediateRender: true },
            index === 0 ? '-=0.3' : '-=0.25'
          );
        });
        editor.from(q('[data-editor-cursor]'), { x: -120, y: 90, autoAlpha: 0, duration: 0.9, ease: 'power2.out' }, '-=0.6');
        editor.from(q('[data-editor-plus]'), { scale: 0, transformOrigin: '50% 50%', duration: 0.35, ease: 'back.out(2)' });

        q('[data-doodle]').forEach((el, index) => {
          gsap.from(el, {
            autoAlpha: 0,
            scale: 0.7,
            duration: 0.8,
            delay: 0.6 + index * 0.15,
            ease: 'back.out(1.6)'
          });
          gsap.to(el, {
            y: index % 2 === 0 ? -14 : 12,
            rotation: index % 2 === 0 ? 6 : -5,
            duration: 3 + index * 0.6,
            ease: 'sine.inOut',
            yoyo: true,
            repeat: -1
          });
        });

        const typewriter = q('[data-typewriter]')[0];
        if (typewriter && prompts.length > 1) {
          startTypewriter(typewriter, prompts);
        }

        q('[data-reveal]').forEach((el) => {
          gsap.from(el, {
            y: 40,
            autoAlpha: 0,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: { trigger: el, scroller: root, start: 'top 85%', once: true }
          });
        });

        q('svg[data-illustration]').forEach((svg) => animateIllustration(svg, root));

        q('[data-mermaid-demo]').forEach((demo) => {
          mermaidTimeline(demo, {
            scrollTrigger: { trigger: demo, scroller: root, start: 'top 75%', once: true }
          });
        });
      });

      // The page itself is the scroll container. Only decorative layers drift,
      // never interactive content or body copy. Skip touch widths and reduced motion.
      mm.add('(min-width: 768px) and (prefers-reduced-motion: no-preference)', () => {
        q('[data-parallax]').forEach((element) => {
          const raw = Number(element.getAttribute('data-parallax'));
          const travel = Number.isFinite(raw) ? Math.max(-90, Math.min(90, raw)) : 35;
          gsap.fromTo(element, { y: -travel }, {
            y: travel, ease: 'none',
            scrollTrigger: {
              trigger: element.closest('[data-parallax-section]') ?? element.closest('section') ?? element,
              scroller: root,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 0.65,
              invalidateOnRefresh: true
            }
          });
        });
      });

      return () => mm.revert();
    },
    { scope, dependencies: [prompts.join('\n')], revertOnUpdate: true }
  );
}
